import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, ArrowsClockwise, ImageBroken, Lightning, GearSix, Pencil, Check, X, Camera, Trash, NotePencil } from '@phosphor-icons/react';
import { fetchProduct, configureProduct, fetchPrice, getPricingConfig, updatePricingConfig, updateManualPrice, updateProduct, uploadProductImage, getFileUrl, syncOneConfig } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

function PriceBadge({ value, tier }) {
  if (value === null || value === undefined) return null;
  const cls = {
    vendor: 'price-badge-vendor',
    retail: 'bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]',
    website: 'bg-[#E0F2FE] text-[#0369A1] border border-[#BAE6FD]',
    reseller: 'bg-[#DCFCE7] text-[#15803D] border border-[#BBF7D0]',
  }[tier] || 'price-badge-vendor';
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${cls}`}>${value.toFixed(2)}</span>;
}

function EditablePrice({ value, tier, quantity, productId, configKey, currentOptions, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const num = parseFloat(inputVal);
    if (isNaN(num) || num < 0) { toast.error('Enter a valid price'); return; }
    setSaving(true);
    try {
      // Always send the live, currently-selected options. Backend derives config_key from
      // them — this avoids any race when the user switches options before the price refetch settles.
      const payload = { quantity, config_key: configKey || '', options: currentOptions || {} };
      const fieldMap = { vendor: 'vendor_price', retail: 'retail_price', website: 'website_price', reseller: 'reseller_price' };
      payload[fieldMap[tier]] = num;
      await updateManualPrice(productId, payload);
      toast.success(`${tier} price updated for this configuration`);
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-[#94A3B8] text-xs">$</span>
        <Input value={inputVal} onChange={e => setInputVal(e.target.value)}
          type="number" step="0.01" min="0" autoFocus className="w-20 h-7 text-xs px-1.5"
          onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
          data-testid={`edit-price-input-${tier}-${quantity}`} />
        <button onClick={handleSave} disabled={saving}
          className="w-6 h-6 rounded bg-green-50 text-green-600 flex items-center justify-center hover:bg-green-100">
          <Check size={12} weight="bold" />
        </button>
        <button onClick={() => setEditing(false)}
          className="w-6 h-6 rounded bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-slate-100">
          <X size={12} weight="bold" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 group">
      <PriceBadge value={value} tier={tier} />
      <button onClick={() => { setInputVal(value?.toFixed(2) || ''); setEditing(true); }}
        data-testid={`edit-price-btn-${tier}-${quantity}`}
        className="w-5 h-5 rounded text-[#CBD5E1] hover:text-[#2563EB] hover:bg-blue-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
        <Pencil size={11} />
      </button>
    </div>
  );
}

function PricingConfigPanel({ productId, isSqftPricing, vendorSqftRate, sizePresets, onConfigChange, onSizePresetsSaved }) {
  const [retailPct, setRetailPct] = useState('200');
  const [websitePct, setWebsitePct] = useState('100');
  const [resellerPct, setResellerPct] = useState('50');
  const [vendorRate, setVendorRate] = useState('');
  const [retailRate, setRetailRate] = useState('');
  const [websiteRate, setWebsiteRate] = useState('');
  const [resellerRate, setResellerRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [savingPresets, setSavingPresets] = useState(false);

  useEffect(() => {
    getPricingConfig(productId).then(cfg => {
      setRetailPct(String(cfg.retail_percent ?? 200));
      setWebsitePct(String(cfg.website_percent ?? 100));
      setResellerPct(String(cfg.reseller_percent ?? 50));
      setVendorRate(cfg.vendor_sqft_rate != null ? String(cfg.vendor_sqft_rate) : '');
      setRetailRate(cfg.retail_sqft_rate != null ? String(cfg.retail_sqft_rate) : '');
      setWebsiteRate(cfg.website_sqft_rate != null ? String(cfg.website_sqft_rate) : '');
      setResellerRate(cfg.reseller_sqft_rate != null ? String(cfg.reseller_sqft_rate) : '');
    }).catch(() => {});
  }, [productId]);

  useEffect(() => {
    setPresets((sizePresets || []).map(p => ({
      value: p.value || '',
      label: p.label || p.value || '',
      width: p.width != null ? String(p.width) : '',
      height: p.height != null ? String(p.height) : '',
    })));
  }, [sizePresets]);

  const addPreset = () => setPresets(p => [...p, { value: '', label: '', width: '', height: '' }]);
  const removePreset = (i) => setPresets(p => p.filter((_, idx) => idx !== i));
  const applyDefaultPresets = () => setPresets([
    { value: '12x18', label: '12x18', width: '12', height: '18' },
    { value: '14x24', label: '14x24', width: '14', height: '24' },
    { value: '18x24', label: '18x24', width: '18', height: '24' },
  ]);
  const updatePreset = (i, field, val) => setPresets(p => p.map((row, idx) => {
    if (idx !== i) return row;
    const next = { ...row, [field]: val };
    // Auto-fill value from W/H when value is empty.
    if ((field === 'width' || field === 'height') && !row.value) {
      const w = field === 'width' ? val : next.width;
      const h = field === 'height' ? val : next.height;
      if (w && h) { next.value = `${w}x${h}`; if (!next.label) next.label = `${w}x${h}`; }
    }
    return next;
  }));

  const handleSavePresets = async () => {
    setSavingPresets(true);
    try {
      const cleaned = presets
        .map(p => ({
          value: (p.value || '').trim(),
          label: (p.label || p.value || '').trim(),
          width: parseFloat(p.width),
          height: parseFloat(p.height),
        }))
        .filter(p => p.value && p.width > 0 && p.height > 0);
      await updateProduct(productId, { size_presets: cleaned });
      toast.success(cleaned.length ? `${cleaned.length} size preset${cleaned.length === 1 ? '' : 's'} saved` : 'Size presets cleared (vendor sizes restored)');
      if (onSizePresetsSaved) onSizePresetsSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save sizes');
    } finally { setSavingPresets(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        retail_percent: parseFloat(retailPct) || 200,
        website_percent: parseFloat(websitePct) || 100,
        reseller_percent: parseFloat(resellerPct) || 50,
      };
      if (isSqftPricing) {
        payload.vendor_sqft_rate = vendorRate === '' ? 0 : parseFloat(vendorRate);
        payload.retail_sqft_rate = retailRate === '' ? 0 : parseFloat(retailRate);
        payload.website_sqft_rate = websiteRate === '' ? 0 : parseFloat(websiteRate);
        payload.reseller_sqft_rate = resellerRate === '' ? 0 : parseFloat(resellerRate);
      }
      await updatePricingConfig(productId, payload);
      toast.success('Pricing settings saved');
      if (onConfigChange) onConfigChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4">
      <button data-testid="toggle-pricing-config" onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-sm font-medium text-[#2563EB] hover:text-[#1D4ED8] transition-colors">
        <GearSix size={16} weight="bold" />
        {open ? 'Hide' : 'Show'} Pricing Settings
      </button>
      {open && (
        <div className="mt-3 p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] space-y-4 animate-fade-in" data-testid="pricing-config-panel">
          <div>
            <h4 className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit' }}>Default Markup Percentages</h4>
            <p className="text-xs text-[#94A3B8] mb-2">Applied to vendor price. Used when no sq-ft rate is set (per tier).</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-[#DC2626] font-semibold">Retail %</Label>
                <Input data-testid="retail-pct-input" type="number" value={retailPct} onChange={e => setRetailPct(e.target.value)}
                  className="h-9 text-sm mt-1" placeholder="200" />
              </div>
              <div>
                <Label className="text-xs text-[#0369A1] font-semibold">Website %</Label>
                <Input data-testid="website-pct-input" type="number" value={websitePct} onChange={e => setWebsitePct(e.target.value)}
                  className="h-9 text-sm mt-1" placeholder="100" />
              </div>
              <div>
                <Label className="text-xs text-[#15803D] font-semibold">Reseller %</Label>
                <Input data-testid="reseller-pct-input" type="number" value={resellerPct} onChange={e => setResellerPct(e.target.value)}
                  className="h-9 text-sm mt-1" placeholder="50" />
              </div>
            </div>
          </div>

          {isSqftPricing && (
            <div className="pt-3 border-t border-[#E2E8F0]">
              <h4 className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit' }}>Per Sq-Ft Rates (Custom-Size Pricing)</h4>
              <p className="text-xs text-[#94A3B8] mb-2">
                $ per sq ft for each tier. If set, overrides the markup % for that tier. Leave blank (or 0) to use markup %. Setting a Vendor rate also bypasses the live vendor lookup entirely.
              </p>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs text-[#475569] font-semibold">Vendor $/sqft</Label>
                  <Input data-testid="vendor-sqft-input" type="number" step="0.01" value={vendorRate} onChange={e => setVendorRate(e.target.value)}
                    className="h-9 text-sm mt-1" placeholder={vendorSqftRate != null ? `live ~$${vendorSqftRate.toFixed(2)}` : 'e.g. 2.50'} />
                </div>
                <div>
                  <Label className="text-xs text-[#DC2626] font-semibold">Retail $/sqft</Label>
                  <Input data-testid="retail-sqft-input" type="number" step="0.01" value={retailRate} onChange={e => setRetailRate(e.target.value)}
                    className="h-9 text-sm mt-1" placeholder="e.g. 6.00" />
                </div>
                <div>
                  <Label className="text-xs text-[#0369A1] font-semibold">Website $/sqft</Label>
                  <Input data-testid="website-sqft-input" type="number" step="0.01" value={websiteRate} onChange={e => setWebsiteRate(e.target.value)}
                    className="h-9 text-sm mt-1" placeholder="e.g. 4.50" />
                </div>
                <div>
                  <Label className="text-xs text-[#15803D] font-semibold">Reseller $/sqft</Label>
                  <Input data-testid="reseller-sqft-input" type="number" step="0.01" value={resellerRate} onChange={e => setResellerRate(e.target.value)}
                    className="h-9 text-sm mt-1" placeholder="e.g. 3.50" />
                </div>
              </div>
              {vendorRate === '' && vendorSqftRate == null && (
                <p className="text-xs text-[#94A3B8] mt-2 italic">Tip: enter a custom size and click Get Pricing to see the live vendor sq-ft reference, or set a Vendor $/sqft to skip vendor lookups entirely.</p>
              )}
            </div>
          )}

          {isSqftPricing && (
            <div className="pt-3 border-t border-[#E2E8F0]" data-testid="size-presets-editor">
              <h4 className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit' }}>Default Sizes</h4>
              <p className="text-xs text-[#94A3B8] mb-2">
                Override the vendor's preset sizes. Each preset uses your per-sqft rates above. Leave empty to use the vendor's sizes. The "Custom Size" option is always available alongside.
              </p>
              <div className="space-y-2">
                {presets.map((p, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`size-preset-row-${i}`}>
                    <Input data-testid={`preset-label-${i}`} value={p.label} onChange={e => updatePreset(i, 'label', e.target.value)}
                      placeholder='Label (e.g. 12"x18")' className="h-9 text-sm flex-1" />
                    <Input data-testid={`preset-width-${i}`} type="number" step="0.5" min="0" value={p.width} onChange={e => updatePreset(i, 'width', e.target.value)}
                      placeholder="W" className="h-9 text-sm w-20" />
                    <span className="text-[#94A3B8] text-xs">×</span>
                    <Input data-testid={`preset-height-${i}`} type="number" step="0.5" min="0" value={p.height} onChange={e => updatePreset(i, 'height', e.target.value)}
                      placeholder="H" className="h-9 text-sm w-20" />
                    <span className="text-xs text-[#94A3B8]">in</span>
                    <button type="button" onClick={() => removePreset(i)} data-testid={`remove-preset-${i}`}
                      className="w-8 h-8 rounded text-[#94A3B8] hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors">
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Button type="button" data-testid="add-size-preset-btn" onClick={addPreset} size="sm" variant="outline" className="h-9 text-xs">
                  + Add size
                </Button>
                <Button type="button" data-testid="apply-default-sizes-btn" onClick={applyDefaultPresets} size="sm" variant="outline" className="h-9 text-xs">
                  Use defaults (12×18, 14×24, 18×24)
                </Button>
                <Button type="button" data-testid="save-size-presets-btn" onClick={handleSavePresets} disabled={savingPresets} size="sm"
                  className="h-9 px-4 text-xs bg-[#0F172A] hover:bg-[#1E293B] text-white">
                  {savingPresets ? 'Saving...' : 'Save Sizes'}
                </Button>
              </div>
            </div>
          )}

          <Button data-testid="save-pricing-config-btn" onClick={handleSave} disabled={saving} size="sm"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg h-9 px-4 text-sm font-semibold">
            {saving ? 'Saving...' : 'Save Pricing Settings'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [selections, setSelections] = useState({});
  const [prices, setPrices] = useState([]);
  const [configKey, setConfigKey] = useState('');
  const [needsSync, setNeedsSync] = useState(false);
  const [syncingConfig, setSyncingConfig] = useState(false);
  const [tierAccess, setTierAccess] = useState(user?.tier_access || ['vendor', 'retail', 'website', 'reseller']);
  const [imgErr, setImgErr] = useState(false);
  const isAdmin = user?.role === 'admin';
  const [editingDesc, setEditingDesc] = useState(false);
  const [descValue, setDescValue] = useState('');
  const [editingTurnaround, setEditingTurnaround] = useState(false);
  const [turnaroundOverrides, setTurnaroundOverrides] = useState({});
  const [designFee, setDesignFee] = useState(null);
  const [designFeeRetail, setDesignFeeRetail] = useState(null);
  const [designFeeWebsite, setDesignFeeWebsite] = useState(null);
  const [designFeeReseller, setDesignFeeReseller] = useState(null);
  const [editingDesignFee, setEditingDesignFee] = useState(false);
  const [dfVendor, setDfVendor] = useState('');
  const [dfRetail, setDfRetail] = useState('');
  const [dfWebsite, setDfWebsite] = useState('');
  const [dfReseller, setDfReseller] = useState('');
  const [designOption, setDesignOption] = useState('client'); // 'client' or 'us'

  useEffect(() => {
    fetchProduct(productId).then(p => {
      setProduct(p);
      if (p.turnaround_overrides) setTurnaroundOverrides(p.turnaround_overrides);
      if (p.design_fee != null) { setDesignFee(p.design_fee); setDfVendor(String(p.design_fee)); }
      if (p.design_fee_retail != null) { setDesignFeeRetail(p.design_fee_retail); setDfRetail(String(p.design_fee_retail)); }
      if (p.design_fee_website != null) { setDesignFeeWebsite(p.design_fee_website); setDfWebsite(String(p.design_fee_website)); }
      if (p.design_fee_reseller != null) { setDesignFeeReseller(p.design_fee_reseller); setDfReseller(String(p.design_fee_reseller)); }
      if (p.configurator?.options) {
        setConfig(p.configurator);
        initSelections(p.configurator);
      }
    }).catch(() => toast.error('Product not found')).finally(() => setLoading(false));
  }, [productId]);

  const initSelections = (cfg) => {
    const sel = {};
    for (const [, opt] of Object.entries(cfg.options || {})) {
      if (opt.type === 'input') {
        sel[opt.name] = opt.default || '1';
      } else if (opt.type === 'custom_size') {
        sel[opt.width_name] = opt.width_default || '1';
        sel[opt.height_name] = opt.height_default || '1';
      } else {
        const selected = opt.options?.find(o => o.selected);
        if (selected) sel[opt.name] = selected.value;
        else if (opt.options?.[0]) sel[opt.name] = opt.options[0].value;
      }
    }
    setSelections({ ...(cfg.hidden_fields || {}), ...sel });
  };

  const handleLoadConfig = async () => {
    setConfigLoading(true);
    try {
      const cfg = await configureProduct(productId);
      setConfig(cfg);
      initSelections(cfg);
      setProduct(prev => ({ ...prev, configurator: cfg }));
      toast.success('Product options loaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load options');
    } finally { setConfigLoading(false); }
  };

  const handleGetPrice = useCallback(async (overrideSel) => {
    const sel = overrideSel || selections;
    if (!config) return;
    // Enforce vendor size restrictions before hitting the API.
    const cs = config?.options?.custom_size;
    const sizeOpt = config?.options?.size;
    if (cs && sizeOpt?.has_custom && sel[sizeOpt?.name] === '-1') {
      const w = parseFloat(sel[cs.width_name]);
      const h = parseFloat(sel[cs.height_name]);
      const minW = parseFloat(cs.min_width) || null;
      const maxW = parseFloat(cs.max_width) || null;
      const minH = parseFloat(cs.min_height) || null;
      const maxH = parseFloat(cs.max_height) || null;
      if (cs.restrict_width && !isNaN(w) && ((minW && w < minW) || (maxW && w > maxW))) {
        setPrices([]);
        toast.error(`Width must be between ${minW}″ and ${maxW}″`);
        return;
      }
      if (cs.restrict_height && !isNaN(h) && ((minH && h < minH) || (maxH && h > maxH))) {
        setPrices([]);
        toast.error(`Height must be between ${minH}″ and ${maxH}″`);
        return;
      }
    }
    setPriceLoading(true);
    try {
      const result = await fetchPrice(productId, sel);
      setPrices(result.prices || []);
      setNeedsSync(!!result.needs_sync);
      if (result.tier_access) setTierAccess(result.tier_access);
      if (result.config_key) setConfigKey(result.config_key);
      if (result.prices?.length === 0 && !result.needs_sync) toast.info('No pricing available');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch price');
    } finally { setPriceLoading(false); }
  }, [productId, selections, config]);

  const handleSyncThisConfig = async () => {
    setSyncingConfig(true);
    try {
      const r = await syncOneConfig(productId, selections);
      toast.success(`Synced from vendor (${r.rows} row${r.rows === 1 ? '' : 's'})`);
      await handleGetPrice();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed. Try again.');
    } finally { setSyncingConfig(false); }
  };

  const handleOptionChange = (name, value) => {
    const newSel = { ...selections, [name]: value };
    setSelections(newSel);
    handleGetPrice(newSel);
  };

  if (loading) return <div className="flex min-h-screen bg-[#F8FAFC]"><Sidebar /><main className="flex-1 p-8"><Skeleton className="h-96 rounded-xl" /></main></div>;
  if (!product) return <div className="flex min-h-screen bg-[#F8FAFC]"><Sidebar /><main className="flex-1 p-8 flex items-center justify-center"><p className="text-[#94A3B8]">Product not found</p></main></div>;

  const opts = config?.options || {};
  const show = { vendor: tierAccess.includes('vendor'), retail: tierAccess.includes('retail'), website: tierAccess.includes('website'), reseller: tierAccess.includes('reseller') };

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]" data-testid="product-page">
      <Sidebar />
      <main className="flex-1 p-6 md:p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          <button data-testid="back-btn" onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#2563EB] mb-6 transition-colors">
            <ArrowLeft size={16} /> Back to Products
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left - Images & Description */}
            <div className="animate-fade-in">
              {/* Main Image */}
              <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 flex items-center justify-center aspect-square shadow-sm relative">
                {product.image_url && !imgErr ? (
                  <img src={getFileUrl(product.image_url)} alt={product.name} className="max-w-full max-h-full object-contain" onError={() => setImgErr(true)} />
                ) : isAdmin ? (
                  <label className="flex flex-col items-center gap-3 cursor-pointer text-center" data-testid="upload-empty-main">
                    <div className="w-16 h-16 rounded-2xl bg-[#EFF6FF] flex items-center justify-center">
                      <Camera size={28} className="text-[#2563EB]" />
                    </div>
                    <span className="text-sm font-medium text-[#475569]">Click to upload main image</span>
                    <span className="text-xs text-[#94A3B8]">JPG, PNG, WebP — max 5MB</span>
                    <input type="file" accept="image/*" className="sr-only" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const result = await uploadProductImage(productId, file);
                        // Persist the bare storage path; the frontend signs the URL fresh
                        // at every render via getFileUrl(...). Storing a fully signed URL
                        // breaks once the embedded JWT expires.
                        await updateProduct(productId, { image_url: result.path });
                        setProduct(prev => ({ ...prev, image_url: result.path }));
                        setImgErr(false);
                        toast.success('Main image uploaded');
                      } catch { toast.error('Upload failed'); }
                    }} />
                  </label>
                ) : <ImageBroken size={80} className="text-[#CBD5E1]" />}
                {isAdmin && (
                  <div className="absolute bottom-3 right-3 flex gap-2">
                    {product.image_url && (
                      <button data-testid="delete-main-image" onClick={async () => {
                        try {
                          await updateProduct(productId, { image_url: '' });
                          setProduct(prev => ({ ...prev, image_url: '' }));
                          toast.success('Main image removed');
                        } catch { toast.error('Failed'); }
                      }}
                      className="w-9 h-9 rounded-lg bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                      title="Delete main image">
                        <Trash size={16} weight="bold" />
                      </button>
                    )}
                    <label className="w-9 h-9 rounded-lg bg-[#2563EB] text-white flex items-center justify-center cursor-pointer hover:bg-[#1D4ED8] transition-colors shadow-md"
                      title="Upload main image" data-testid="upload-main-image">
                      <Camera size={16} weight="bold" />
                      <input type="file" accept="image/*" className="sr-only" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const result = await uploadProductImage(productId, file);
                          await updateProduct(productId, { image_url: result.path });
                          setProduct(prev => ({ ...prev, image_url: result.path }));
                          setImgErr(false);
                          toast.success('Main image updated');
                        } catch { toast.error('Upload failed'); }
                      }} />
                    </label>
                  </div>
                )}
              </div>

              {/* Additional Images */}
              {(product.additional_images?.length > 0) && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  {product.additional_images.map((img, i) => (
                    <div key={i} className="w-16 h-16 rounded-lg border border-[#E2E8F0] overflow-hidden bg-[#F8FAFC] relative group">
                      <img src={getFileUrl(img)} alt="" className="w-full h-full object-cover" />
                      {isAdmin && (
                        <button onClick={async () => {
                          const imgs = product.additional_images.filter((_, idx) => idx !== i);
                          await updateProduct(productId, { additional_images: imgs });
                          setProduct(prev => ({ ...prev, additional_images: imgs }));
                          toast.success('Image removed');
                        }}
                        className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash size={14} className="text-white" />
                        </button>
                      )}
                    </div>
                  ))}
                  {isAdmin && product.additional_images.length < 4 && (
                    <label className="w-16 h-16 rounded-lg border-2 border-dashed border-[#CBD5E1] flex items-center justify-center cursor-pointer hover:border-[#2563EB] transition-colors"
                      data-testid="upload-additional-image">
                      <Camera size={16} className="text-[#CBD5E1]" />
                      <input type="file" accept="image/*" className="sr-only" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const result = await uploadProductImage(productId, file);
                          const imgs = [...(product.additional_images || []), result.path];
                          await updateProduct(productId, { additional_images: imgs });
                          setProduct(prev => ({ ...prev, additional_images: imgs }));
                          toast.success('Image uploaded');
                        } catch { toast.error('Upload failed'); }
                      }} />
                    </label>
                  )}
                </div>
              )}
              {isAdmin && !product.additional_images?.length && (
                <div className="mt-3">
                  <label className="flex items-center gap-2 text-sm text-[#2563EB] hover:text-[#1D4ED8] cursor-pointer transition-colors"
                    data-testid="upload-first-image">
                    <Camera size={16} /> Add product images (up to 4)
                    <input type="file" accept="image/*" className="sr-only" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const result = await uploadProductImage(productId, file);
                        await updateProduct(productId, { additional_images: [result.path] });
                        setProduct(prev => ({ ...prev, additional_images: [result.path] }));
                        toast.success('Image uploaded');
                      } catch { toast.error('Upload failed'); }
                    }} />
                  </label>
                </div>
              )}

              {/* Description - editable for admin */}
              <div className="mt-4 bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit' }}>Description</h3>
                  {isAdmin && !editingDesc && (
                    <button data-testid="edit-desc-btn" onClick={() => { setEditingDesc(true); setDescValue(product.description || ''); }}
                      className="text-[#94A3B8] hover:text-[#2563EB] transition-colors"><NotePencil size={16} /></button>
                  )}
                </div>
                {editingDesc ? (
                  <div>
                    <textarea data-testid="desc-textarea" value={descValue} onChange={e => setDescValue(e.target.value)}
                      className="w-full h-32 p-3 border border-[#E2E8F0] rounded-lg text-sm text-[#475569] resize-y focus:ring-2 focus:ring-[#3B82F6] focus:outline-none"
                      placeholder="Enter product description..." />
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" data-testid="save-desc-btn" onClick={async () => {
                        try {
                          await updateProduct(productId, { description: descValue });
                          setProduct(prev => ({ ...prev, description: descValue }));
                          setEditingDesc(false);
                          toast.success('Description saved');
                        } catch { toast.error('Failed'); }
                      }} className="h-8 bg-[#2563EB] text-white text-xs">Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingDesc(false)} className="h-8 text-xs">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-[#475569] leading-relaxed whitespace-pre-wrap">
                    {product.description || <span className="text-[#CBD5E1] italic">No description yet</span>}
                  </p>
                )}
              </div>

              {/* Turnaround Options Editor - edit vendor labels */}
              {isAdmin && config?.options && (() => {
                const turnaroundOpt = Object.entries(config.options).find(([, opt]) => opt.label?.toLowerCase().includes('turnaround'));
                if (!turnaroundOpt) return null;
                const [tKey, tOpt] = turnaroundOpt;
                return (
                  <div className="mt-4 bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit' }}>Edit Turnaround Labels</h3>
                      {!editingTurnaround && (
                        <button data-testid="edit-turnaround-btn" onClick={() => setEditingTurnaround(true)}
                          className="text-[#94A3B8] hover:text-[#2563EB] transition-colors"><NotePencil size={16} /></button>
                      )}
                    </div>
                    {editingTurnaround ? (
                      <div className="space-y-2">
                        {tOpt.options?.filter(o => o.value).map(o => (
                          <div key={o.value} className="flex items-center gap-2">
                            <span className="text-xs text-[#94A3B8] w-36 truncate" title={o.label}>{o.label}</span>
                            <span className="text-[#CBD5E1]">&rarr;</span>
                            <Input
                              data-testid={`turnaround-override-${o.value}`}
                              value={turnaroundOverrides[o.value] ?? ''}
                              onChange={e => setTurnaroundOverrides(prev => ({ ...prev, [o.value]: e.target.value }))}
                              placeholder={o.label}
                              className="h-8 text-sm flex-1"
                            />
                          </div>
                        ))}
                        <div className="flex gap-2 mt-2">
                          <Button size="sm" data-testid="save-turnaround-btn" onClick={async () => {
                            try {
                              // Filter out empty overrides
                              const cleaned = {};
                              for (const [k, v] of Object.entries(turnaroundOverrides)) {
                                if (v && v.trim()) cleaned[k] = v.trim();
                              }
                              await updateProduct(productId, { turnaround_overrides: cleaned });
                              setProduct(prev => ({ ...prev, turnaround_overrides: cleaned }));
                              setEditingTurnaround(false);
                              toast.success('Turnaround labels saved');
                            } catch { toast.error('Failed'); }
                          }} className="h-8 bg-[#2563EB] text-white text-xs">Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingTurnaround(false)} className="h-8 text-xs">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {tOpt.options?.filter(o => o.value).map(o => (
                          <div key={o.value} className="flex items-center gap-2 text-sm">
                            <span className="text-[#94A3B8]">{o.label}</span>
                            {turnaroundOverrides[o.value] && (
                              <><span className="text-[#CBD5E1]">&rarr;</span><span className="text-[#0F172A] font-medium">{turnaroundOverrides[o.value]}</span></>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Right */}
            <div className="animate-fade-in" style={{ animationDelay: '100ms' }}>
              <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 shadow-sm">
                <h1 className="text-2xl font-bold tracking-tight text-[#0F172A] mb-1" style={{ fontFamily: 'Outfit' }} data-testid="product-name">
                  {product.name}
                </h1>
                <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F1F5F9] text-[#475569] border border-[#E2E8F0]">
                  {product.category}
                </span>

                {!config ? (
                  <div className="mt-6">
                    <p className="text-sm text-[#475569] mb-3">Load product options to see pricing and variants.</p>
                    <Button data-testid="load-options-btn" onClick={handleLoadConfig} disabled={configLoading}
                      className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg h-10 px-5 font-semibold flex items-center gap-2">
                      <ArrowsClockwise size={18} weight="bold" className={configLoading ? 'animate-spin' : ''} />
                      {configLoading ? 'Loading...' : 'Load Options'}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="mt-6 space-y-4" data-testid="product-configurator">
                      <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider" style={{ fontFamily: 'Outfit' }}>Price Calculator</h3>
                      {Object.entries(opts).map(([key, opt]) => {
                        // Custom size inputs (width x height)
                        if (opt.type === 'custom_size') {
                          const sizeOpt = opts.size;
                          const isCustom = sizeOpt?.has_custom && selections[sizeOpt?.name] === '-1';
                          if (!sizeOpt?.has_custom) return null; // Only show if size has custom option
                          const wVal = parseFloat(selections[opt.width_name]);
                          const hVal = parseFloat(selections[opt.height_name]);
                          const minW = parseFloat(opt.min_width) || null;
                          const maxW = parseFloat(opt.max_width) || null;
                          const minH = parseFloat(opt.min_height) || null;
                          const maxH = parseFloat(opt.max_height) || null;
                          const wErr = opt.restrict_width && !isNaN(wVal) && ((minW && wVal < minW) || (maxW && wVal > maxW));
                          const hErr = opt.restrict_height && !isNaN(hVal) && ((minH && hVal < minH) || (maxH && hVal > maxH));
                          const sqft = (!isNaN(wVal) && !isNaN(hVal) && wVal > 0 && hVal > 0) ? (wVal * hVal) / 144 : null;
                          return (
                            <div key={key} className={isCustom ? '' : 'hidden'}>
                              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">{opt.label}</label>
                              <div className="flex items-center gap-2">
                                <Input
                                  data-testid="custom-width-input"
                                  type="number" min={minW || 1} max={maxW || undefined} step="0.5"
                                  value={selections[opt.width_name] || opt.width_default || '1'}
                                  onChange={e => handleOptionChange(opt.width_name, e.target.value)}
                                  className={`flex-1 h-10 rounded-lg ${wErr ? 'border-red-400 focus-visible:ring-red-400' : 'border-[#E2E8F0]'}`}
                                  placeholder="Width"
                                />
                                <span className="text-[#94A3B8] font-medium">x</span>
                                <Input
                                  data-testid="custom-height-input"
                                  type="number" min={minH || 1} max={maxH || undefined} step="0.5"
                                  value={selections[opt.height_name] || opt.height_default || '1'}
                                  onChange={e => handleOptionChange(opt.height_name, e.target.value)}
                                  className={`flex-1 h-10 rounded-lg ${hErr ? 'border-red-400 focus-visible:ring-red-400' : 'border-[#E2E8F0]'}`}
                                  placeholder="Height"
                                />
                                <span className="text-xs text-[#94A3B8]">in</span>
                              </div>
                              {(opt.restrict_width || opt.restrict_height) && (
                                <div className="mt-1 flex items-center justify-between text-xs">
                                  <span className={wErr || hErr ? 'text-red-600 font-medium' : 'text-[#94A3B8]'}>
                                    {opt.restrict_width && minW && maxW ? `Width: ${minW}–${maxW}″` : ''}
                                    {opt.restrict_width && opt.restrict_height ? ' · ' : ''}
                                    {opt.restrict_height && minH && maxH ? `Height: ${minH}–${maxH}″` : ''}
                                  </span>
                                  {sqft !== null && (
                                    <span className="text-[#475569] font-medium">{sqft.toFixed(2)} sq ft</span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }
                        // Text input (e.g. quantity)
                        if (opt.type === 'input') {
                          return (
                            <div key={key}>
                              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">{opt.label}</label>
                              <Input
                                data-testid={`option-${key}`}
                                type="number" min="1"
                                value={selections[opt.name] || opt.default || '1'}
                                onChange={e => handleOptionChange(opt.name, e.target.value)}
                                className="w-full h-10 rounded-lg border-[#E2E8F0]"
                              />
                            </div>
                          );
                        }
                        // Size dropdown with optional "Custom Size" entry
                        if (key === 'size' && opt.has_custom) {
                          const allOpts = [...(opt.options || []), { value: '-1', label: 'Custom Size' }];
                          return (
                            <div key={key}>
                              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">{opt.label}</label>
                              <Select value={selections[opt.name] || ''} onValueChange={v => handleOptionChange(opt.name, v)}>
                                <SelectTrigger data-testid={`option-${key}`} className="w-full h-10 rounded-lg border-[#E2E8F0]">
                                  <SelectValue placeholder={`Select ${opt.label}`} />
                                </SelectTrigger>
                                <SelectContent>
                                  {allOpts.filter(o => o.value !== '').map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        }
                        // Standard select dropdown
                        if (!opt.options?.length) return null;
                        const isTurnaround = opt.label?.toLowerCase().includes('turnaround');
                        return (
                          <div key={key}>
                            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">{opt.label}</label>
                            <Select value={selections[opt.name] || ''} onValueChange={v => handleOptionChange(opt.name, v)}>
                              <SelectTrigger data-testid={`option-${key}`} className="w-full h-10 rounded-lg border-[#E2E8F0]">
                                <SelectValue placeholder={`Select ${opt.label}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {opt.options.filter(o => o.value !== '').map(o => {
                                  const displayLabel = isTurnaround && turnaroundOverrides[o.value] ? turnaroundOverrides[o.value] : o.label;
                                  return <SelectItem key={o.value} value={o.value}>{displayLabel}</SelectItem>;
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}

                      {/* Design Fees Dropdown - after all vendor options */}
                      <div>
                        <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Design</label>
                        <Select value={designOption} onValueChange={setDesignOption}>
                          <SelectTrigger data-testid="option-design" className="w-full h-10 rounded-lg border-[#E2E8F0]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="client">Design Provided by Client</SelectItem>
                            <SelectItem value="us">We Need to Design</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <Button data-testid="get-price-btn" onClick={() => handleGetPrice()} disabled={priceLoading}
                        className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg h-11 font-semibold flex items-center justify-center gap-2 mt-4">
                        <Lightning size={18} weight="fill" />
                        {priceLoading ? 'Fetching Prices...' : 'Get Pricing'}
                      </Button>
                    </div>

                    {isAdmin && <PricingConfigPanel
                      productId={productId}
                      isSqftPricing={product.is_sqft_pricing}
                      sizePresets={product.size_presets}
                      vendorSqftRate={(() => {
                        const row = prices[0];
                        if (!row || !row.sqft || row.sqft <= 0 || !row.vendor_price) return null;
                        return row.vendor_price / row.sqft / (row.quantity || 1);
                      })()}
                      onConfigChange={() => handleGetPrice()}
                      onSizePresetsSaved={async () => {
                        // Re-fetch product so the size dropdown rebuilds with the new presets.
                        try {
                          const fresh = await fetchProduct(productId);
                          setProduct(fresh);
                          if (fresh.configurator?.options) {
                            setConfig(fresh.configurator);
                            initSelections(fresh.configurator);
                            setPrices([]);
                          }
                        } catch { /* noop */ }
                      }}
                    />}

                    {/* Price Table */}
                    {priceLoading ? (
                      <div className="mt-6 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 rounded-lg" />)}</div>                    ) : prices.length > 0 ? (
                      <div className="mt-6" data-testid="price-table">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-[#0F172A] uppercase tracking-wider" style={{ fontFamily: 'Outfit' }}>Pricing Tiers</h3>
                          {isAdmin && <p className="text-[10px] text-[#94A3B8]">Hover a price &amp; click pencil to edit</p>}
                        </div>
                        <div className="rounded-xl border border-[#E2E8F0] overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-[#F8FAFC]">
                                <TableHead className="text-xs font-semibold tracking-wider uppercase text-[#94A3B8]">Qty</TableHead>
                                {show.vendor && <TableHead className="text-xs font-semibold tracking-wider uppercase text-[#475569]">Vendor</TableHead>}
                                {show.retail && <TableHead className="text-xs font-semibold tracking-wider uppercase text-[#DC2626]">Retail</TableHead>}
                                {show.website && <TableHead className="text-xs font-semibold tracking-wider uppercase text-[#0369A1]">Website</TableHead>}
                                {show.reseller && <TableHead className="text-xs font-semibold tracking-wider uppercase text-[#15803D]">Reseller</TableHead>}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {prices.map((p, i) => (
                                <TableRow key={i} className={`transition-colors ${p.selected ? 'bg-[#EFF6FF] border-l-2 border-l-[#2563EB]' : 'hover:bg-[#F8FAFC]'}`}
                                  data-testid={`price-row-${p.quantity}`}>
                                  <TableCell className={`font-semibold text-sm ${p.selected ? 'text-[#2563EB]' : 'text-[#0F172A]'}`}>
                                    {p.quantity.toLocaleString()}
                                  </TableCell>
                                  {show.vendor && <TableCell>
                                    {isAdmin ? <EditablePrice value={p.vendor_price} tier="vendor" quantity={p.quantity} productId={productId} configKey={configKey} currentOptions={selections} onSaved={() => handleGetPrice()} />
                                    : <PriceBadge value={p.vendor_price} tier="vendor" />}
                                  </TableCell>}
                                  {show.retail && <TableCell>
                                    {isAdmin ? <EditablePrice value={p.retail_price} tier="retail" quantity={p.quantity} productId={productId} configKey={configKey} currentOptions={selections} onSaved={() => handleGetPrice()} />
                                    : <PriceBadge value={p.retail_price} tier="retail" />}
                                  </TableCell>}
                                  {show.website && <TableCell>
                                    {isAdmin ? <EditablePrice value={p.website_price} tier="website" quantity={p.quantity} productId={productId} configKey={configKey} currentOptions={selections} onSaved={() => handleGetPrice()} />
                                    : <PriceBadge value={p.website_price} tier="website" />}
                                  </TableCell>}
                                  {show.reseller && <TableCell>
                                    {isAdmin ? <EditablePrice value={p.reseller_price} tier="reseller" quantity={p.quantity} productId={productId} configKey={configKey} currentOptions={selections} onSaved={() => handleGetPrice()} />
                                    : <PriceBadge value={p.reseller_price} tier="reseller" />}
                                  </TableCell>}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    ) : null}

                    {/* Needs-sync banner — shown when this configuration isn't in the local DB yet */}
                    {!priceLoading && needsSync && (
                      <div className="mt-6 p-5 rounded-xl border border-dashed border-amber-300 bg-amber-50" data-testid="needs-sync-banner">
                        <div className="flex items-start gap-3">
                          <div className="text-amber-600 mt-0.5">
                            <ArrowsClockwise size={20} weight="bold" />
                          </div>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-amber-900" style={{ fontFamily: 'Outfit' }}>
                              No saved pricing for this configuration
                            </h4>
                            <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                              {isAdmin
                                ? "This combination hasn't been pulled from the vendor yet. Click below to fetch and save it permanently — next time it will load instantly from the database."
                                : "Pricing for this combination isn't available yet. Please contact an admin to sync it."}
                            </p>
                            {isAdmin && (
                              <Button
                                data-testid="sync-this-config-btn"
                                onClick={handleSyncThisConfig}
                                disabled={syncingConfig}
                                size="sm"
                                className="mt-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg h-9 px-4 text-sm font-semibold flex items-center gap-2"
                              >
                                <ArrowsClockwise size={16} weight="bold" className={syncingConfig ? 'animate-spin' : ''} />
                                {syncingConfig ? 'Fetching from Vendor...' : 'Fetch from Vendor (this config)'}
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Design Fee Row - shown below pricing table */}
                    {prices.length > 0 && (
                      <div className="mt-4 rounded-xl border border-[#E2E8F0] overflow-hidden" data-testid="design-fees-section">
                        <div className="bg-[#F8FAFC] px-4 py-2.5 border-b border-[#E2E8F0] flex items-center justify-between">
                          <h3 className="text-xs font-semibold text-[#0F172A] uppercase tracking-wider" style={{ fontFamily: 'Outfit' }}>
                            Design Fee {designOption === 'client' ? '(Client Provided)' : '(We Design)'}
                          </h3>
                          {isAdmin && !editingDesignFee && (
                            <button data-testid="edit-design-fee-btn" onClick={() => {
                              setEditingDesignFee(true);
                              setDfVendor(String(designFee || ''));
                              setDfRetail(String(designFeeRetail || ''));
                              setDfWebsite(String(designFeeWebsite || ''));
                              setDfReseller(String(designFeeReseller || ''));
                            }} className="text-[10px] text-[#2563EB] hover:text-[#1D4ED8] font-medium">Edit</button>
                          )}
                        </div>
                        {/* Admin edit row */}
                        {editingDesignFee && isAdmin && (
                          <div className="px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0]">
                            <p className="text-[10px] text-[#94A3B8] mb-2">"We Need to Design" fee per tier</p>
                            <div className="grid grid-cols-4 gap-2">
                              {show.vendor && <div><Label className="text-[10px] text-[#475569]">Vendor</Label><Input data-testid="df-vendor" type="number" step="0.01" min="0" value={dfVendor} onChange={e => setDfVendor(e.target.value)} className="h-7 text-xs mt-0.5" /></div>}
                              {show.retail && <div><Label className="text-[10px] text-[#DC2626]">Retail</Label><Input data-testid="df-retail" type="number" step="0.01" min="0" value={dfRetail} onChange={e => setDfRetail(e.target.value)} className="h-7 text-xs mt-0.5" /></div>}
                              {show.website && <div><Label className="text-[10px] text-[#0369A1]">Website</Label><Input data-testid="df-website" type="number" step="0.01" min="0" value={dfWebsite} onChange={e => setDfWebsite(e.target.value)} className="h-7 text-xs mt-0.5" /></div>}
                              {show.reseller && <div><Label className="text-[10px] text-[#15803D]">Reseller</Label><Input data-testid="df-reseller" type="number" step="0.01" min="0" value={dfReseller} onChange={e => setDfReseller(e.target.value)} className="h-7 text-xs mt-0.5" /></div>}
                            </div>
                            <div className="flex gap-2 mt-2">
                              <Button size="sm" data-testid="save-design-fee-btn" onClick={async () => {
                                try {
                                  const v = parseFloat(dfVendor) || 0, r = parseFloat(dfRetail) || 0, w = parseFloat(dfWebsite) || 0, s = parseFloat(dfReseller) || 0;
                                  await updateProduct(productId, { design_fee: v, design_fee_retail: r, design_fee_website: w, design_fee_reseller: s });
                                  setDesignFee(v); setDesignFeeRetail(r); setDesignFeeWebsite(w); setDesignFeeReseller(s);
                                  setEditingDesignFee(false);
                                  toast.success('Design fees saved');
                                } catch { toast.error('Failed'); }
                              }} className="h-7 bg-[#2563EB] text-white text-[10px] px-3">Save</Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingDesignFee(false)} className="h-7 text-[10px]">Cancel</Button>
                            </div>
                          </div>
                        )}
                        {/* Fee display row */}
                        <div className="px-4 py-3 flex items-center" data-testid={`design-fee-${designOption}`}>
                          {designOption === 'client' ? (
                            <div className="flex items-center gap-4 w-full justify-around">
                              {show.vendor && <div className="text-center"><p className="text-[10px] text-[#94A3B8] uppercase">Vendor</p><span className="inline-flex px-3 py-1 rounded-full text-sm font-semibold bg-[#DCFCE7] text-[#15803D] border border-[#BBF7D0]">$0.00</span></div>}
                              {show.retail && <div className="text-center"><p className="text-[10px] text-[#94A3B8] uppercase">Retail</p><span className="inline-flex px-3 py-1 rounded-full text-sm font-semibold bg-[#DCFCE7] text-[#15803D] border border-[#BBF7D0]">$0.00</span></div>}
                              {show.website && <div className="text-center"><p className="text-[10px] text-[#94A3B8] uppercase">Website</p><span className="inline-flex px-3 py-1 rounded-full text-sm font-semibold bg-[#DCFCE7] text-[#15803D] border border-[#BBF7D0]">$0.00</span></div>}
                              {show.reseller && <div className="text-center"><p className="text-[10px] text-[#94A3B8] uppercase">Reseller</p><span className="inline-flex px-3 py-1 rounded-full text-sm font-semibold bg-[#DCFCE7] text-[#15803D] border border-[#BBF7D0]">$0.00</span></div>}
                            </div>
                          ) : (
                            <div className="flex items-center gap-4 w-full justify-around">
                              {show.vendor && <div className="text-center"><p className="text-[10px] text-[#94A3B8] uppercase">Vendor</p>{designFee ? <span className="inline-flex px-3 py-1 rounded-full text-sm font-semibold price-badge-vendor">${designFee.toFixed(2)}</span> : <span className="text-xs text-[#94A3B8]">-</span>}</div>}
                              {show.retail && <div className="text-center"><p className="text-[10px] text-[#DC2626] uppercase">Retail</p>{designFeeRetail ? <span className="inline-flex px-3 py-1 rounded-full text-sm font-semibold bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]">${designFeeRetail.toFixed(2)}</span> : <span className="text-xs text-[#94A3B8]">-</span>}</div>}
                              {show.website && <div className="text-center"><p className="text-[10px] text-[#0369A1] uppercase">Website</p>{designFeeWebsite ? <span className="inline-flex px-3 py-1 rounded-full text-sm font-semibold bg-[#E0F2FE] text-[#0369A1] border border-[#BAE6FD]">${designFeeWebsite.toFixed(2)}</span> : <span className="text-xs text-[#94A3B8]">-</span>}</div>}
                              {show.reseller && <div className="text-center"><p className="text-[10px] text-[#15803D] uppercase">Reseller</p>{designFeeReseller ? <span className="inline-flex px-3 py-1 rounded-full text-sm font-semibold bg-[#DCFCE7] text-[#15803D] border border-[#BBF7D0]">${designFeeReseller.toFixed(2)}</span> : <span className="text-xs text-[#94A3B8]">-</span>}</div>}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
